import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page404 from "src/pages/Page404";
import DynamicDiningPromo from "src/pages/promotions/dining/DynamicDiningPromo";
import { resolveRestaurantPromoPage } from "src/utils/restaurantPromoUtils";

/**
 * Dynamic dining promo LP — Payload when `restaurant-promotions` is in
 * REACT_APP_PAYLOAD_SOURCES, otherwise JSON locales (legacy diningPromos.json).
 */
const DynamicDiningPromoPage = () => {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [resolved, setResolved] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setIsNotFound(false);
      try {
        const page = await resolveRestaurantPromoPage(location, i18n.language);
        if (cancelled) return;
        if (page) {
          setResolved(page);
        } else {
          setResolved(null);
          setIsNotFound(true);
        }
      } catch (error) {
        console.error("Failed to load restaurant promo content:", error);
        if (!cancelled) {
          setResolved(null);
          setIsNotFound(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, i18n.language]);

  if (isLoading) {
    return (
      <LoadingStatus>
        <Spinner animation="border" variant="light" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </LoadingStatus>
    );
  }

  if (isNotFound || !resolved) return <Page404 />;

  const promoKey =
    resolved.mode === "payload" ? resolved.page?.promotionKey : resolved.promoKey;
  const wrapEarlyBird = promoKey === "earlyBirdBreakfastSpecial";

  const promo =
    resolved.mode === "payload" ? (
      <DynamicDiningPromo
        promoData={resolved.page}
        scheduleKeyName={
          resolved.page.unpublishAt ? undefined : resolved.page.promotionKey
        }
        unpublishAt={resolved.page.unpublishAt}
        theme={resolved.page.theme}
      />
    ) : (
      <DynamicDiningPromo
        promoKey={resolved.promoKey}
        scheduleKeyName={resolved.promoKey}
      />
    );

  return wrapEarlyBird ? <EarlyBirdShell>{promo}</EarlyBirdShell> : promo;
};

const LoadingStatus = styled.div`
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--black);
`;

/** Rail logo is an SVG — constrain hero size (same as former EarlyBirdBreakfastSpecial). */
const EarlyBirdShell = styled.div`
  .hero_container {
    min-height: 0;
  }
  .hero_image_container {
    padding: 1.5rem 1rem 1rem;
  }
  .hero_image {
    max-width: 220px !important;
    width: auto;
    margin: 0 auto;
    padding: 0 !important;
    @media (min-width: 768px) {
      max-width: 280px !important;
    }
    img {
      max-height: 220px;
      width: auto;
      margin: 0 auto;
      @media (min-width: 768px) {
        max-height: 280px;
      }
    }
  }
`;

export default DynamicDiningPromoPage;
